
import { useCallback, useEffect, useRef } from 'react'
import type { Node, Edge, ReactFlowInstance } from '@xyflow/react'
import { toastWithUndo } from '@/lib/canvas/toast-undo'
import { useJobStore } from '@/stores/jobStore'
import { blocksRepeatedRun } from '@/lib/canvas/workspace-commands'

/**
 * useShortcuts - Canvas keyboard shortcuts
 *
 * Works WITHOUT ReactFlowProvider context. Instead, receives
 * setNodes/setEdges/canvasRef from the parent component.
 */

const GRID_SNAP = 20
const NUDGE_LARGE = 60

interface ShortcutDeps {
  enabled?: boolean
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  canvasRef: React.RefObject<ReactFlowInstance | null>
}

interface ShortcutCallbacks {
  onUndo?: () => void
  onRedo?: () => void
  onToggleShortcuts?: () => void
  onToggleProperties?: () => void
  onToggleSidebar?: () => void
  onToggleAllSidebars?: () => void
  onAddNode?: (type: 'text' | 'image' | 'video' | 'sound' | 'shader') => void
  onVariantSwitch?: (variantIndex: number) => void
  onOpenCommandPalette?: () => void
  onOpenLibrary?: () => void
}

export function useShortcuts(deps: ShortcutDeps, callbacks: ShortcutCallbacks = {}) {
  const { setNodes, setEdges, canvasRef, enabled = true } = deps
  const { onUndo, onRedo, onToggleShortcuts, onToggleProperties, onToggleSidebar, onToggleAllSidebars, onAddNode, onVariantSwitch, onOpenCommandPalette, onOpenLibrary } = callbacks

  // ── Selection ──────────────────────────────────────────────

  const deleteSelected = useCallback(() => {
    let deletedCount = 0
    setNodes((prev) => {
      const selectedNodeIds = new Set(prev.filter((n) => n.selected).map((n) => n.id))
      if (selectedNodeIds.size === 0) return prev

      deletedCount = selectedNodeIds.size

      setEdges((prevEdges) =>
        prevEdges.filter(
          (e) =>
            !e.selected &&
            !selectedNodeIds.has(e.source) &&
            !selectedNodeIds.has(e.target),
        ),
      )

      // Clean up job store entries for deleted nodes
      const jobStore = useJobStore.getState()
      selectedNodeIds.forEach(nodeId => {
        const jobs = jobStore.getJobsByNode(nodeId)
        jobs.forEach(job => {
          jobStore.removePlaceholders(job.id)
          jobStore.removeJob(job.id)
        })
      })

      return prev.filter((n) => !selectedNodeIds.has(n.id))
    })

    // Show undo toast after state update
    requestAnimationFrame(() => {
      if (deletedCount > 0 && onUndo) {
        toastWithUndo(`Deleted ${deletedCount} node${deletedCount > 1 ? 's' : ''}`, onUndo)
      }
    })
  }, [setNodes, setEdges, onUndo])

  const selectAll = useCallback(() => {
    setNodes((prev) => prev.map((n) => ({ ...n, selected: true })))
  }, [setNodes])

  const deselectAll = useCallback(() => {
    setNodes((prev) => prev.map((n) => ({ ...n, selected: false })))
    setEdges((prev) => prev.map((e) => ({ ...e, selected: false })))
  }, [setNodes, setEdges])

  // ── Tab cycling ────────────────────────────────────────────

  const cycleSelection = useCallback(
    (direction: 'next' | 'prev') => {
      setNodes((prev) => {
        if (prev.length === 0) return prev

        const sorted = [...prev].sort((a, b) => {
          const rowA = Math.round(a.position.y / GRID_SNAP)
          const rowB = Math.round(b.position.y / GRID_SNAP)
          if (rowA !== rowB) return rowA - rowB
          return a.position.x - b.position.x
        })

        const currentIndex = sorted.findIndex((n) => n.selected)

        let nextIndex: number
        if (currentIndex === -1) {
          nextIndex = 0
        } else if (direction === 'next') {
          nextIndex = (currentIndex + 1) % sorted.length
        } else {
          nextIndex = (currentIndex - 1 + sorted.length) % sorted.length
        }

        const nextId = sorted[nextIndex].id

        // Focus the DOM node for visible focus ring
        requestAnimationFrame(() => {
          document.querySelector<HTMLElement>(`[data-id="${nextId}"]`)?.focus()
        })

        return prev.map((n) => ({ ...n, selected: n.id === nextId }))
      })
    },
    [setNodes],
  )

  // ── Duplicate ──────────────────────────────────────────────

  const duplicateSelected = useCallback(() => {
    setNodes((prev) => {
      const selectedNodes = prev.filter((n) => n.selected)
      if (selectedNodes.length === 0) return prev

      const selectedNodeIds = new Set(selectedNodes.map((n) => n.id))
      const timestamp = Date.now()
      const suffix = Math.random().toString(36).substring(2, 7)

      const idMap = new Map<string, string>()

      const newNodes: Node[] = selectedNodes.map((node, i) => {
        const newId = `${node.id}-dup-${timestamp}-${suffix}-${i}`
        idMap.set(node.id, newId)
        const clonedNode = {
          ...structuredClone(node),
          id: newId,
          position: { x: node.position.x + GRID_SNAP, y: node.position.y + GRID_SNAP },
          selected: true,
        }
        if (clonedNode.parentId && idMap.has(clonedNode.parentId)) {
          clonedNode.parentId = idMap.get(clonedNode.parentId)
        }
        return clonedNode
      })

      // Duplicate edges between selected nodes
      setEdges((prevEdges) => {
        const newEdges = prevEdges
          .filter((e) => selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target))
          .map((edge, i) => {
            const newSource = idMap.get(edge.source)
            const newTarget = idMap.get(edge.target)
            if (!newSource || !newTarget) return null
            return {
              ...structuredClone(edge),
              id: `${edge.id}-dup-${timestamp}-${suffix}-${i}`,
              source: newSource,
              target: newTarget,
            }
          })
          .filter(Boolean) as Edge[]
        return [...prevEdges, ...newEdges]
      })

      return [
        ...prev.map((n) => ({ ...n, selected: false })),
        ...newNodes,
      ]
    })
  }, [setNodes, setEdges])

  // ── Arrow key nudge ────────────────────────────────────────

  const nudgeSelected = useCallback(
    (dx: number, dy: number) => {
      setNodes((prev) =>
        prev.map((n) =>
          n.selected
            ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
            : n,
        ),
      )
    },
    [setNodes],
  )

  // ── Center on selected (Cmd+E) ────────────────────────────

  const centerOnSelected = useCallback(() => {
    const instance = canvasRef.current
    if (!instance) return

    const selected = instance.getNodes().filter((n: Node) => n.selected)
    if (selected.length === 0) return

    instance.fitView({
      nodes: selected,
      duration: 300,
      padding: 0.5,
    })
  }, [canvasRef])

  // ── Lock/unlock (Cmd+L) ───────────────────────────────────

  const toggleLock = useCallback(() => {
    setNodes((prev) => {
      const selected = prev.filter((n) => n.selected)
      if (selected.length === 0) return prev

      const anyUnlocked = selected.some((n) => !n.data?.locked)

      return prev.map((n) =>
        n.selected
          ? { ...n, draggable: !anyUnlocked, data: { ...n.data, locked: anyUnlocked } }
          : n,
      )
    })
  }, [setNodes])

  // ── Z-index (Cmd+] / Cmd+[) ───────────────────────────────

  const changeZIndex = useCallback(
    (delta: number) => {
      setNodes((prev) =>
        prev.map((n) =>
          n.selected
            ? { ...n, zIndex: (n.zIndex ?? 0) + delta }
            : n,
        ),
      )
    },
    [setNodes],
  )

  // ── Clipboard (Copy/Paste/Cut) ─────────────────────────────
  // Note: clipboard is handled inline here instead of a separate hook
  // to avoid multiple global keydown listeners

  const clipboardRef = useRef<{ nodes: Node[]; edges: Edge[]; pasteCount: number }>({
    nodes: [],
    edges: [],
    pasteCount: 0,
  })

  const copySelected = useCallback(() => {
    setNodes((prev) => {
      const selectedNodes = prev.filter((n) => n.selected)
      if (selectedNodes.length === 0) return prev

      const selectedNodeIds = new Set(selectedNodes.map((n) => n.id))

      setEdges((prevEdges) => {
        const selectedEdges = prevEdges.filter(
          (e) => selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target),
        )
        clipboardRef.current = {
          nodes: selectedNodes.map((n) => structuredClone(n)),
          edges: selectedEdges.map((e) => structuredClone(e)),
          pasteCount: 0,
        }
        return prevEdges // don't modify edges
      })

      return prev // don't modify nodes
    })
  }, [setNodes, setEdges])

  const paste = useCallback(() => {
    const { nodes: clipNodes, edges: clipEdges } = clipboardRef.current
    if (clipNodes.length === 0) return

    clipboardRef.current.pasteCount++
    const count = clipboardRef.current.pasteCount
    const timestamp = Date.now()
    const suffix = Math.random().toString(36).substring(2, 9)

    const idMap = new Map<string, string>()

    const newNodes: Node[] = clipNodes.map((node, i) => {
      const newId = `${node.id}-copy-${timestamp}-${suffix}-${i}`
      idMap.set(node.id, newId)
      const clonedNode = {
        ...structuredClone(node),
        id: newId,
        position: {
          x: node.position.x + GRID_SNAP * count,
          y: node.position.y + GRID_SNAP * count,
        },
        selected: true,
      }
      if (clonedNode.parentId && idMap.has(clonedNode.parentId)) {
        clonedNode.parentId = idMap.get(clonedNode.parentId)
      }
      return clonedNode
    })

    const newEdges: Edge[] = clipEdges
      .map((edge, i) => {
        const newSource = idMap.get(edge.source)
        const newTarget = idMap.get(edge.target)
        if (!newSource || !newTarget) return null
        return {
          ...structuredClone(edge),
          id: `${edge.id}-copy-${timestamp}-${suffix}-${i}`,
          source: newSource,
          target: newTarget,
          selected: false,
        }
      })
      .filter((e): e is NonNullable<typeof e> => e !== null) as Edge[]

    setNodes((prev) => [
      ...prev.map((n) => ({ ...n, selected: false })),
      ...newNodes,
    ])
    setEdges((prev) => [...prev, ...newEdges])
  }, [setNodes, setEdges])

  const cut = useCallback(() => {
    let cutCount = 0
    setNodes((prev) => {
      const selectedNodes = prev.filter((n) => n.selected)
      if (selectedNodes.length === 0) return prev

      cutCount = selectedNodes.length
      const selectedNodeIds = new Set(selectedNodes.map((n) => n.id))

      setEdges((prevEdges) => {
        const selectedEdges = prevEdges.filter(
          (e) => selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target),
        )
        clipboardRef.current = {
          nodes: selectedNodes.map((n) => structuredClone(n)),
          edges: selectedEdges.map((e) => structuredClone(e)),
          pasteCount: 0,
        }
        return prevEdges.filter(
          (e) => !selectedNodeIds.has(e.source) && !selectedNodeIds.has(e.target),
        )
      })

      return prev.filter((n) => !selectedNodeIds.has(n.id))
    })

    requestAnimationFrame(() => {
      if (cutCount > 0 && onUndo) {
        toastWithUndo(`Cut ${cutCount} node${cutCount > 1 ? 's' : ''}`, onUndo)
      }
    })
  }, [setNodes, setEdges, onUndo])

  // ── Main key handler ───────────────────────────────────────

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Controls own their keyboard interaction, including sliders and portalled menus.
      if (event.defaultPrevented) return
      const target = event.target as HTMLElement
      if (target.closest('input, textarea, select, button, [contenteditable="true"], [role="slider"], [role="spinbutton"], [role="combobox"], [role="menu"], [role="listbox"]')) {
        // Exception: zoom shortcuts should ALWAYS work (prevent browser zoom)
        const mod = event.ctrlKey || event.metaKey
        if (mod && (event.key === '=' || event.key === '+')) {
          event.preventDefault()
          canvasRef.current?.zoomIn()
        } else if (mod && event.key === '-') {
          event.preventDefault()
          canvasRef.current?.zoomOut()
        }
        return
      }

      // Don't intercept when modal/dialog is open
      if (target.closest('[role="dialog"], .modal, [data-modal="true"]')) return

      const mod = event.ctrlKey || event.metaKey
      const shift = event.shiftKey

      switch (true) {
        // ── Delete ──
        case event.key === 'Delete' || event.key === 'Backspace':
          event.preventDefault()
          deleteSelected()
          break

        // ── Select all (Cmd+A) ──
        case mod && event.key === 'a':
          event.preventDefault()
          selectAll()
          break

        // ── Duplicate (Cmd+D) ──
        case mod && event.key === 'd':
          event.preventDefault()
          duplicateSelected()
          break

        // ── Copy (Cmd+C) ──
        case mod && event.key === 'c':
          event.preventDefault()
          copySelected()
          break

        // ── Paste (Cmd+V) ──
        case mod && event.key === 'v':
          event.preventDefault()
          paste()
          break

        // ── Cut (Cmd+X) ──
        case mod && event.key === 'x':
          event.preventDefault()
          cut()
          break

        // ── Escape ──
        case event.key === 'Escape':
          deselectAll()
          break

        // ── Undo (Cmd+Z) ──
        case mod && !shift && event.key === 'z':
          event.preventDefault()
          onUndo?.()
          break

        // ── Redo (Cmd+Shift+Z) ──
        case mod && shift && event.key === 'z':
          event.preventDefault()
          onRedo?.()
          break

        // ── Tab / Shift+Tab — Cycle through nodes ──
        case event.key === 'Tab':
          event.preventDefault()
          cycleSelection(shift ? 'prev' : 'next')
          break

        // ── Arrow keys — Nudge selected nodes ──
        case event.key === 'ArrowUp':
          event.preventDefault()
          nudgeSelected(0, shift ? -NUDGE_LARGE : -GRID_SNAP)
          break
        case event.key === 'ArrowDown':
          event.preventDefault()
          nudgeSelected(0, shift ? NUDGE_LARGE : GRID_SNAP)
          break
        case event.key === 'ArrowLeft':
          event.preventDefault()
          nudgeSelected(shift ? -NUDGE_LARGE : -GRID_SNAP, 0)
          break
        case event.key === 'ArrowRight':
          event.preventDefault()
          nudgeSelected(shift ? NUDGE_LARGE : GRID_SNAP, 0)
          break

        // ── Center on selected (Cmd+E) ──
        case mod && event.key === 'e':
          event.preventDefault()
          centerOnSelected()
          break

        // ── Lock/unlock (Cmd+L) ──
        case mod && event.key === 'l':
          event.preventDefault()
          toggleLock()
          break

        // ── Z-index forward (Cmd+]) ──
        case mod && event.key === ']':
          event.preventDefault()
          changeZIndex(1)
          break

        // ── Z-index backward (Cmd+[) ──
        case mod && event.key === '[':
          event.preventDefault()
          changeZIndex(-1)
          break

        // ── Zoom in (Cmd+=) ──
        case mod && (event.key === '=' || event.key === '+'):
          event.preventDefault()
          canvasRef.current?.zoomIn()
          break

        // ── Zoom out (Cmd+-) ──
        case mod && event.key === '-':
          event.preventDefault()
          canvasRef.current?.zoomOut()
          break

        // ── Fit view (Cmd+0) ──
        case mod && event.key === '0':
          event.preventDefault()
          canvasRef.current?.fitView({ duration: 300 })
          break

        // ── Generate selected node (Cmd+Enter). A held key must not submit again. ──
        case mod && event.key === 'Enter':
          if (blocksRepeatedRun(event)) return
          event.preventDefault()
          document.dispatchEvent(new CustomEvent('genie:generate-selected'))
          break

        // ── Rename selected node (F2) ──
        case event.key === 'F2':
          event.preventDefault()
          document.dispatchEvent(new CustomEvent('genie:rename-selected'))
          break

        // ── Command palette (Cmd+K) ──
        case mod && event.key === 'k':
          event.preventDefault()
          onOpenCommandPalette?.()
          break

        // ── Shortcuts overlay (Cmd+/) ──
        case mod && event.key === '/':
          event.preventDefault()
          onToggleShortcuts?.()
          break

        // ── UI Panel shortcuts (single key, no modifier) ──

        case !mod && !shift && event.key === 'p':
          event.preventDefault()
          onToggleProperties?.()
          break

        // ── Library (L) ──
        case !mod && !shift && event.key === 'l':
          event.preventDefault()
          onOpenLibrary?.()
          break

        case !mod && !shift && !event.altKey && event.key === '\\':
          event.preventDefault()
          onToggleSidebar?.()
          break

        // ── Toggle all sidebars (Cmd+\) ──
        case mod && event.key === '\\':
          event.preventDefault()
          onToggleAllSidebars?.()
          break

        // ── Variant switch (number keys 1-5 on selected node) ──
        case !mod && !shift && event.key >= '1' && event.key <= '5':
          event.preventDefault()
          onVariantSwitch?.(parseInt(event.key, 10))
          break

        // ── Quick-add node shortcuts (single key) ──

        case !mod && !shift && event.key === 't':
          event.preventDefault()
          onAddNode?.('text')
          break

        case !mod && !shift && event.key === 'i':
          event.preventDefault()
          onAddNode?.('image')
          break

        case !mod && !shift && event.key === 'v':
          event.preventDefault()
          onAddNode?.('video')
          break

        case !mod && !shift && event.key === 's':
          event.preventDefault()
          onAddNode?.('sound')
          break

        case !mod && !shift && event.key === 'o':
          event.preventDefault()
          onAddNode?.('shader')
          break
      }
    },
    [
      deleteSelected, selectAll, deselectAll, duplicateSelected,
      copySelected, paste, cut,
      cycleSelection, nudgeSelected, centerOnSelected, toggleLock,
      changeZIndex,
      canvasRef,
      onUndo, onRedo, onToggleShortcuts, onOpenCommandPalette, onOpenLibrary,
      onToggleProperties, onToggleSidebar, onToggleAllSidebars, onAddNode, onVariantSwitch,
    ],
  )

  // Attach global keyboard listener
  useEffect(() => {
    if (!enabled) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown, enabled])

  // Custom event listeners for node context menu actions
  useEffect(() => {
    if (!enabled) return
    const handleDelete = () => deleteSelected()
    const handleDuplicate = () => duplicateSelected()
    document.addEventListener('genie:delete-selected', handleDelete)
    document.addEventListener('genie:duplicate-selected', handleDuplicate)
    return () => {
      document.removeEventListener('genie:delete-selected', handleDelete)
      document.removeEventListener('genie:duplicate-selected', handleDuplicate)
    }
  }, [deleteSelected, duplicateSelected, enabled])

  return {
    deleteSelected,
    selectAll,
    deselectAll,
    duplicateSelected,
    copySelected,
    paste,
    cut,
    cycleSelection,
    nudgeSelected,
    centerOnSelected,
    toggleLock,
    changeZIndex,
  }
}
